/**
 * CLI Authorization (doractl browser login) consent page.
 *
 * doractl opens the browser to `/#/cli-auth?redirect=...&challenge=...&state=...`.
 * This page reuses the admin's existing Supabase session, shows a consent
 * screen, then exchanges the live session for a one-time code via
 * POST /v1/auth/cli/code and redirects the browser back to the CLI's
 * loopback callback with `?code=<code>&state=<state>`.
 *
 * Security: the `redirect` target is restricted to loopback hosts
 * (127.0.0.1 / localhost) to prevent open-redirect abuse.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { baseApiClient, HttpError } from '@/lib/api/client';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { useI18n } from '@/i18n';

const LOOPBACK_HOSTS = new Set(['127.0.0.1', 'localhost']);

interface CliAuthParams {
  redirect: string;
  challenge: string;
  state: string;
}

/** Only allow http loopback redirect targets — prevents open redirect. */
function isLoopbackRedirect(redirect: string): boolean {
  try {
    const url = new URL(redirect);
    if (url.protocol !== 'http:') {
      return false;
    }
    return LOOPBACK_HOSTS.has(url.hostname);
  } catch {
    return false;
  }
}

function appendQuery(redirect: string, params: Record<string, string>): string {
  const url = new URL(redirect);
  for (const [key, value] of Object.entries(params)) {
    url.searchParams.set(key, value);
  }
  return url.toString();
}

export const CliAuthPage: React.FC = () => {
  const { t } = useI18n();
  const navigate = useNavigate();

  const [authorizing, setAuthorizing] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [account, setAccount] = useState<string | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null);

  // Parse and validate URL query params once.
  const parsed = useMemo<
    { ok: true; params: CliAuthParams } | { ok: false; error: string }
  >(() => {
    // Hash router: params live in the hash segment (#/cli-auth?redirect=...).
    const hash = window.location.hash;
    const queryIndex = hash.indexOf('?');
    const search = queryIndex >= 0 ? hash.slice(queryIndex + 1) : '';
    const query = new URLSearchParams(search);

    const redirect = query.get('redirect') || '';
    const challenge = query.get('challenge') || '';
    const state = query.get('state') || '';

    if (!redirect || !challenge || !state) {
      return { ok: false, error: 'missingParams' };
    }
    if (!isLoopbackRedirect(redirect)) {
      return { ok: false, error: 'invalidRedirect' };
    }
    return { ok: true, params: { redirect, challenge, state } };
  }, []);

  // Check Supabase session on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;
      const session = data.session;
      setIsLoggedIn(!!session);
      setAccount(session?.user?.email || localStorage.getItem('admin_email'));
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const goToLogin = () => {
    // Preserve the full cli-auth hash (with query) so login returns here.
    const returnTo = window.location.hash.slice(1) || '/cli-auth';
    sessionStorage.setItem('admin_redirect_after_login', returnTo);
    navigate('/login', { replace: true });
  };

  const handleAuthorize = async () => {
    if (!parsed.ok) return;
    setError(null);
    setAuthorizing(true);
    try {
      const { data } = await supabase.auth.getSession();
      const session = data.session;
      if (!session) {
        setIsLoggedIn(false);
        setAuthorizing(false);
        return;
      }

      // Backend mints a fresh, independent CLI session from the authenticated
      // caller (baseApiClient sends the Bearer) — we no longer hand it a copy
      // of this browser's own Supabase tokens.
      const response = await baseApiClient.post<{ code: string; expires_in: number }>(
        '/v1/auth/cli/code',
        {
          code_challenge: parsed.params.challenge,
          state: parsed.params.state,
        }
      );

      const code = response.data.code;
      setRedirecting(true);
      window.location.href = appendQuery(parsed.params.redirect, {
        code,
        state: parsed.params.state,
      });
    } catch (err) {
      const message =
        (err as HttpError)?.message || t('cliAuth.errorAuthorize');
      setError(message);
      setAuthorizing(false);
    }
  };

  const handleCancel = () => {
    if (!parsed.ok) return;
    setRedirecting(true);
    window.location.href = appendQuery(parsed.params.redirect, {
      error: 'access_denied',
      state: parsed.params.state,
    });
  };

  const renderError = (body: React.ReactNode) => (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="max-w-md w-full">
        <Card className="p-8 space-y-4">
          <h2 className="text-xl font-semibold text-foreground">
            {t('cliAuth.errorTitle')}
          </h2>
          {body}
        </Card>
      </div>
    </div>
  );

  if (!parsed.ok) {
    const message =
      parsed.error === 'invalidRedirect'
        ? t('cliAuth.errorInvalidRedirect')
        : t('cliAuth.errorMissingParams');
    return renderError(
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
        {message}
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="max-w-md w-full space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground">
            {t('cliAuth.title')}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {t('cliAuth.subtitle')}
          </p>
        </div>

        <Card className="p-8 space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              {t('cliAuth.consentHeading')}
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {t('cliAuth.consentDescription')}
            </p>
          </div>

          <div className="space-y-3 rounded-lg border border-border bg-muted/40 p-4 text-sm">
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">
                {t('cliAuth.accountLabel')}
              </span>
              <span className="font-medium text-foreground text-right break-all">
                {account || '—'}
              </span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">
                {t('cliAuth.scopeLabel')}
              </span>
              <span className="font-medium text-foreground text-right">
                {t('cliAuth.scopeValue')}
              </span>
            </div>
          </div>

          {error && (
            <div className="rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {error}
            </div>
          )}

          {isLoggedIn === false ? (
            <div className="space-y-4">
              <div className="rounded-lg border border-warning/50 bg-warning/10 px-4 py-3 text-sm text-warning-foreground">
                {t('cliAuth.loginRequired')}
              </div>
              <Button variant="primary" className="w-full" onClick={goToLogin}>
                {t('cliAuth.goToLogin')}
              </Button>
            </div>
          ) : (
            <div className="flex gap-3">
              <Button
                variant="secondary"
                className="flex-1"
                onClick={handleCancel}
                disabled={authorizing || redirecting}
              >
                {t('cliAuth.cancel')}
              </Button>
              <Button
                variant="primary"
                className="flex-1"
                onClick={handleAuthorize}
                loading={authorizing || redirecting}
                disabled={isLoggedIn === null || redirecting}
              >
                {redirecting
                  ? t('cliAuth.redirecting')
                  : authorizing
                    ? t('cliAuth.authorizing')
                    : t('cliAuth.authorize')}
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default CliAuthPage;
